export interface BookingLocations {
  origins: string[];
  destinations: string[];
}

export type TripType = 'one-way' | 'round-trip';
export type TripLeg = 'outbound' | 'return';

export interface SearchCriteria {
  origin: string;
  destination: string;
  date: string;
  pax: number;
  tripType: TripType;
  returnDate?: string;
}

export interface ScheduleAvailability {
  scheduleId: number;
  routeId: number;
  routeName: string;
  origin: string;
  destination: string;
  travelDate: string;
  departureTime: string;
  arrivalTime: string;
  busId: number;
  busModel: string;
  busTypeId: number;
  busTypeName: string;
  baseFare: number | null;
  premiumFare: number | null;
  totalSeats: number;
  availableSeats: number;
}

export interface BookingRoute {
  id: number;
  name: string;
  origin: string;
  destination: string;
  baseFare: number | null;
}

export type SeatStatus = 'available' | 'booked' | 'blocked';
export type SeatType = 'standard' | 'premium' | 'pwd' | 'senior';

export interface SeatMapSeat {
  seatNumber: number;
  row: number;
  column: number;
  type: string;
  status: string;
  price: number | null;
}

export interface Aisle {
  row: number;
  column: number[];
}

export interface ComfortRoom {
  row: number;
  column: number;
}

export interface SeatMap {
  scheduleId: number;
  travelDate: string;
  busId: number;
  seatConfigurationId: number;
  configurationName: string;
  rows: number;
  columns: number;
  aisle: Aisle[];
  cr: ComfortRoom[];
  seats: SeatMapSeat[];
  baseFare: number | null;
  premiumFare: number | null;
  totalSeats: number;
  availableSeats: number;
}

export interface SeatSelection {
  row: number;
  column: number;
}

export interface Passenger {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface ConcessionSelection {
  type: 'student' | 'pwd' | 'senior' | string;
  pax: number;
}

export interface PublicDiscount {
  id: number;
  kind: string;
  title: string;
  description: string | null;
  valueType: string;
  value: number;
  requiresId: boolean;
}

export interface DiscountQuote {
  promoCode: string | null;
  promoDiscount: number;
  concessionDiscount: number;
  discountAmount: number;
  primaryConcessionType: string | null;
  concessionPax: number;
  concessions: { type: string; label: string; pax: number; amount: number }[];
}

export interface CreateBookingRequest {
  passengerId?: number | null;
  scheduleId: number;
  travelDate: string;
  seats: SeatSelection[];
  passenger: Passenger;
  promoCode?: string;
  concessions?: ConcessionSelection[];
  paymentMethod: string;
  deliveryMethod: string;
  agreeToTerms: boolean;
}

export interface BookingSeat {
  seatNumber: number | null;
  row: number;
  column: number;
  type: string;
  price: number;
}

export interface Booking {
  id: number;
  operatorId: number;
  passengerId?: number | null;
  referenceCode: string;
  scheduleId: number;
  routeId: number;
  routeName?: string | null;
  origin?: string | null;
  destination?: string | null;
  departureTime?: string | null;
  arrivalTime?: string | null;
  busTypeName?: string | null;
  busPlateNumber?: string | null;
  travelDate: string;
  pax: number;
  passengerFirstName: string;
  passengerLastName: string;
  passengerEmail: string;
  passengerPhone: string;
  promoCode: string | null;
  subtotalAmount: number;
  bookingFee: number;
  totalAmount: number;
  paymentMethod: string;
  paymentStatus: string;
  deliveryMethod: string;
  status: string;
  seats: BookingSeat[];
  createdAt: string;
  updatedAt: string;
}

export const PAYMENT_METHODS = [
  { id: 'gcash', label: 'GCash', description: 'Pay with GCash wallet', icon: 'smartphone' },
  { id: 'maya', label: 'Maya', description: 'Pay with Maya e-wallet', icon: 'smartphone' },
  { id: 'card', label: 'Credit / Debit Card', description: 'Visa, Mastercard, JCB', icon: 'credit-card' },
] as const;

export const DELIVERY_METHOD_DEFAULT = 'email';
